import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import loaderGif from "../assets/Img/loading1.gif";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import "../assets/CSS/Dashboard.css";

export default function LeatherAutomationDashboard() {

  /* ================= USER ================= */
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const user_type = user.Auth;

  /* ================= UI ================= */
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  /* ================= IMAGE UPLOAD ================= */
  const fileInputRef = useRef(null);
  const uploadLockRef = useRef(false);
  const MAX_FILES = 25;

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  /* ================= VENDOR EXCEL ================= */
  const vendorExcelRef = useRef(null);
  const [vendorExcelUploading, setVendorExcelUploading] = useState(false);

  /* ================= DATA ================= */
  const [compare, setCompare] = useState([]);
  const [summary, setSummary] = useState([]);

  const [selectedSheets, setSelectedSheets] = useState([]);

  const reuploadInputRef = useRef(null);
  const [reUploadingSheet, setReUploadingSheet] = useState(null);

  const [activeTab, setActiveTab] = useState("ALL");

  const [hideFailedAlert, setHideFailedAlert] = useState(false);
  const [hideMismatchAlert, setHideMismatchAlert] = useState(false);

  useEffect(() => {
    setHideFailedAlert(false);
    setHideMismatchAlert(false);
  }, [activeTab]);

  const toggleSheetSelect = (sheetName) => {
    setSelectedSheets(prev =>
      prev.includes(sheetName)
        ? prev.filter(s => s !== sheetName)
        : [...prev, sheetName]
    );
  };

  const isSheetSelected = (sheetName) =>
    selectedSheets.includes(sheetName);

  const [kpi, setKpi] = useState({
    total: 0,
    success: 0,
    successRate: 0
  });

  /* ================= FILTERS ================= */
  const [compareFilters, setCompareFilters] = useState({
    status: "ALL",
    sheet: "",
    minDiff: "",
    maxDiff: ""
  });

  /* ================= RESET FILTERS ================= */
  const resetFilters = () => {
    setCompareFilters({
      status: "ALL",
      sheet: "",
      minDiff: "",
      maxDiff: ""
    });
  };

  /* ================= FILE HANDLERS ================= */
  const addFiles = (incomingFiles) => {
    const mapped = incomingFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      progress: 0
    }));

    setFiles(prev => {
      const map = new Map();
      [...prev, ...mapped].forEach(item => {
        map.set(item.file.name, item); // avoid duplicates
      });

      if (map.size > MAX_FILES) {
        Swal.fire(
          "Limit Exceeded",
          `Max ${MAX_FILES} images allowed`,
          "warning"
        );
        return Array.from(map.values()).slice(0, MAX_FILES);
      }

      return Array.from(map.values());
    });
  };

  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = null;
  };

  const handleReUpload = async (file, sheetName) => {
    if (!file || !sheetName) return;

    const fd = new FormData();
    fd.append("sheetName", sheetName);
    fd.append("image", file);

    try {
      setReUploadingSheet(sheetName);

      Swal.fire({
        title: "Re-uploading Image",
        html: `
          <div style="line-height:1.6">
            Sheet: <b>${sheetName}</b><br/><br/>
            Uploading image & restarting automation…<br/><br/>
            <span style="color:#d9534f">
              Please do not refresh or close this page
            </span>
          </div>
        `,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const res = await axiosInstance.post(
        "/api/leather/reupload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.failed === 0) {
        Swal.fire({
          icon: "success",
          title: "Re-upload Successful",
          html: `
            <b>${sheetName}</b><br/><br/>
            Image processed successfully.<br/>
            Automation completed.
          `
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Re-upload Failed",
          html: `
            <b>${sheetName}</b><br/><br/>
            The image could not be processed due to detection or cropping issues.<br/>
            <br/>
            <b>(Note:</b> Please upload a new image with clear visibility and proper alignment.)</b>
          `
        });
      }

      loadData();
      setSelectedSheets([]);

    } catch (err) {
      //Swal.fire("Error", "Re-upload failed", "error");
      Swal.fire({
        icon: "error",
        title: "Re-upload Error",
        text: err?.response?.data?.message || "Re-upload failed"
      });
    } finally {
      setReUploadingSheet(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleApprove = async (sheetName) => {
    try {
      await axiosInstance.post("/api/leather/approve", {
        sheetName
      });

      Swal.fire("Approved", "Sheet approved successfully", "success");
      loadData(); // DB se fresh data

    } catch (err) {
      Swal.fire("Error", "Approve failed", "error");
    }
  };

  const handleReject = async (sheetName) => {
    try {
      await axiosInstance.post("/api/leather/reject", {
        sheetName
      });

      Swal.fire("Rejected", "Sheet rejected", "warning");
      loadData();

    } catch (err) {
      Swal.fire("Error", "Reject failed", "error");
    }
  };

  const [uploadResult, setUploadResult] = useState({
    total: 0,
    success: 0,
    failed: 0
  });
  
  /* ================= IMAGE UPLOAD ================= */
  const startAutomation = async () => {
    if (uploadLockRef.current || files.length === 0) return;
    uploadLockRef.current = true;

    Swal.fire({
      title: "Processing Images",
      html: `
        <div id="process-text" style="margin-top:10px; line-height:1.6">
          <b>Preparing images...</b><br/><br/>
          Total Images: ${files.length}<br/>
          Current Image: 0 / ${files.length}<br/>
          Current Step: Uploading<br/><br/>
          <span style="color:#d9534f">
            Please do not refresh or close this page
          </span>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      setUploading(true);

      const fd = new FormData();
      files.forEach(item => fd.append("images", item.file));

      const res = await axiosInstance.post(
        "/api/leather/upload-multiple",
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);

            setFiles(prev => prev.map(f => ({ ...f, progress: percent })));

            const el = document.getElementById("process-text");
            if (el) {
              el.innerHTML = `
                <b>Uploading images...</b><br/><br/>
                Total Images: ${files.length}<br/>
                Upload Progress: ${percent}%<br/><br/>
                <span style="color:#666">
                  Next: Auto-crop → AI → Area → DXF → Excel
                </span><br/><br/>
                <span style="color:#d9534f">
                   Please do not refresh or close this page
                </span>
              `;
            }
          }

        }
      );

      if (res.data?.summary) setSummary(res.data.summary);

      setUploadResult({
        total: res.data.total || 0,
        success: res.data.processed || 0,
        failed: res.data.failed || 0
      });

      const failed = res.data.total - res.data.processed;

      /*Swal.fire({
        icon: "info",
        title: "Images Processed Successfully",
        html: `
          <div style="line-height:1.6">
            <b>Upload completed successfully.</b><br/><br/>
            Images are now being processed on the server.<br/>
            This may take a few minutes depending on image size.<br/><br/>
            <span style="color:#d9534f">
              Do not refresh or close this page
            </span>
          </div>
        `
      });*/

      Swal.fire({
        icon: uploadResult.failed > 0 ? "warning" : "success",
        title: "Automation Completed",
        html: `
          <div style="margin-top:10px; line-height:1.6">
            <b>Processing Summary</b><br/><br/>

            Total Images: ${res.data.total}<br/>
            <span style="color:#28a745">
              Success: ${res.data.processed}
            </span><br/>
            <span style="color:#d9534f">
              Failed: ${failed}
            </span><br/><br/>

            ${
              failed > 0
                ? `<span style="color:#d9534f">
                     Some images could not be processed successfully.<br/>
                     The image was either not detected or not cropped correctly.<br/>
                     Please upload a new image for the failed sheets.
                   </span>`
                : `<span style="color:#28a745">
                     All images processed successfully.
                   </span>`
            }<br/>

          </div>
        `,
        allowOutsideClick: false
      });

      setFiles([]);
      loadData();

    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Image upload failed. Please try again."
      });
    } finally {
      setUploading(false);
      uploadLockRef.current = false;
    }
  };

  /* ================= VENDOR EXCEL UPLOAD ================= */
  const uploadVendorExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      Swal.fire({
        title: "Uploading Vendor Excel",
        html: `
          <div style="margin-top:10px">
            <b>Please wait...</b><br/><br/>
            Validating & uploading vendor excel file
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      const fd = new FormData();
      fd.append("file", file);

      await axiosInstance.post(
        "/api/leather/excel-upload-vendor",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire({
        icon: "success",
        title: "Upload Successful",
        text: "Vendor excel uploaded successfully"
      });

      loadData();

    } catch (err) {
      const res = err?.response?.data;

      if (res?.message === "Excel format not supported") {
        Swal.fire({
          icon: "error",
          title: "Invalid Excel Format",
          html: `
            <p><b>Expected Format:</b></p>
            <ul style="text-align:left">
              <li>Column A: Sheet Name</li>
              <li>Column B: Standard Decimeter</li>
              <li>Column C: Colour (optional)</li>
            </ul>
          `
        });
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: res?.message || "Vendor excel upload failed"
      });

    } finally {
      e.target.value = null;
    }
  };


  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    try {
      const res = await axiosInstance.get("/api/leather/compare");
      const data = res.data || [];

      setCompare(data);

      const total = data.length;
      const success = data.filter(d => d.Status === "OK").length;

      setKpi({
        total,
        success,
        successRate: total ? ((success / total) * 100).toFixed(1) : 0
      });

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ================= FILTERED DATA ================= */

  const STATUS_PRIORITY = {
    NOT_PROCESSED: 1,
    FAILED: 2,
    MISMATCH: 3,
    OK_PENDING: 4,
    OK_NOT_APPROVED: 5,
    OK_APPROVED: 6
  };

  const filteredCompare = compare
  .filter(row => {

    /* ===== TAB FILTER ===== */

    if (activeTab === "APPROVED") {
      if (!(row.Status === "OK" && row.IsApproved === true)) return false;
    }

    if (activeTab === "NOT_APPROVED") {
      if (!(row.Status === "OK" && row.IsApproved === false && row.IsReviewed))
        return false;
    }

    if (activeTab === "PENDING") {
      if (!(row.Status === "OK" && row.IsApproved === false && !row.IsReviewed))
        return false;
    }

    if (activeTab === "MISMATCH") {
      if (row.Status !== "MISMATCH") return false;
    }

    if (activeTab === "FAILED") {
      if (row.Status !== "FAILED") return false;
    }

    if (activeTab === "NOT_PROCESSED") {
      if (row.Status !== "NOT_PROCESSED") return false;
    }

    /* ===== SEARCH FILTER ===== */
    if (
      compareFilters.sheet &&
      !row.SheetName?.toLowerCase().includes(compareFilters.sheet.toLowerCase())
    )
      return false;

    /* ===== DIFF FILTER ===== */
    const diff = Number(row.Diff_Percent || 0);

    if (compareFilters.minDiff !== "" && diff < compareFilters.minDiff)
      return false;

    if (compareFilters.maxDiff !== "" && diff > compareFilters.maxDiff)
      return false;

    return true;
  })
  .sort((a, b) => {

    // Sorting ONLY for ALL tab
    if (activeTab !== "ALL") return 0;

    const getPriority = (row) => {
      if (row.Status === "NOT_PROCESSED") return STATUS_PRIORITY.NOT_PROCESSED;
      if (row.Status === "FAILED") return STATUS_PRIORITY.FAILED;
      if (row.Status === "MISMATCH") return STATUS_PRIORITY.MISMATCH;

      if (row.Status === "OK") {
        if (row.IsApproved) return STATUS_PRIORITY.OK_APPROVED;
        if (row.IsReviewed) return STATUS_PRIORITY.OK_NOT_APPROVED;
        return STATUS_PRIORITY.OK_PENDING;
      }

      return 99;
    };

    return getPriority(a) - getPriority(b);
  });



  /* ================= DOWNLOAD COMPARISON EXCEL ================= */
  const downloadComparisonExcel = () => {
    if (filteredCompare.length === 0) {
      Swal.fire("No Data", "Nothing to download", "info");
      return;
    }

    const data = filteredCompare.map((r, i) => {

      let approvalStatus = "N/A";

      if (r.Status === "OK") {
        approvalStatus = r.IsApproved ? "Approved" : "Not Approved";
      }

      return {
        "Sr No": i + 1,
        "Sheet Name": r.SheetName,
        "Vendor Area (SqFt)": r.VendorArea_SqFt,
        "Auto Area (SqFt)": r.AutoArea_SqFt ?? "",
        "Diff %": r.Diff_Percent ?? "",
        "Processing Status": r.Status,
        "Approval Status": approvalStatus
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comparison");

    XLSX.writeFile(wb, "Vendor_vs_Automation_Comparison.xlsx");
  };

  const updateProcessingAlert = (current, total, percent) => {
    Swal.update({
      html: `
        <div style="margin-top:10px; line-height:1.6">
          <b>Processing image ${current} of ${total}</b><br/>
          Uploading… ${percent}%<br/><br/>
          <span style="font-size:13px;color:#666">
            Auto-crop → AI → Area → DXF → Excel
          </span>
        </div>
      `
    });
  };


  /* ================= UI ================= */
  return (
    <div className="d-flex dashboard">

      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4">

          <h3 className="mb-4">Leather Automation Dashboard</h3>

          {/* KPI */}
          <div className="row mb-4">
            {[
              { label: "Total Sheets", value: kpi.total },
              { label: "Success", value: kpi.success, cls: "text-success" },
              {
                label: "Failed",
                value: kpi.total - kpi.success,
                cls: "text-danger"
              },
              { label: "Success %", value: `${kpi.successRate}%`, cls: "text-primary" }
            ]
            .map((k, i) => (
              <div className="col-md-3" key={i}>
                <div className={`card text-center p-3 shadow-sm ${k.cls || ""}`}>
                  <h6>{k.label}</h6>
                  <h3>{k.value}</h3>
                </div>
              </div>
            ))}
          </div>

          {/* UPLOAD */}
          {(user_type === 'Admin' || ["4001","2"].includes(user?.EmployeeID)) && (
            <div className="card p-4 mb-4">
              <h5>Upload Leather Images</h5>

              <div
                className="border rounded p-4 text-center"
                style={{ background: "#f9f9f9" }}
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
              >
                <h6>Drag & Drop Images Here</h6>
                <div className="text-muted mb-2">or</div>

                <input
                  type="file"
                  multiple
                  accept="image/*"
                  ref={fileInputRef}
                  className="form-control"
                  onChange={handleFileChange}
                />

                <div className="text-muted mt-2">
                  Max {MAX_FILES} images allowed
                </div>
              </div>

              {/* IMAGE PREVIEW */}
              {files.length > 0 && (
                <div className="row mt-4">
                  {files.map((item, index) => (
                    <div className="col-md-3 mb-3" key={index}>
                      <div className="card p-2 shadow-sm">
                        <img
                          src={item.preview}
                          alt="preview"
                          className="img-fluid rounded"
                          style={{ height: 150, objectFit: "cover" }}
                        />
                        {/* IMAGE NAME */}
                        <div className="text-truncate mt-1" title={item.file.name}>
                          <small><b>{item.file.name}</b></small>
                        </div>
                        <div className="progress mt-2">
                          <div
                            className="progress-bar"
                            style={{ width: `${item.progress}%` }}
                          >
                            {item.progress}%
                          </div>
                        </div>

                        {item.progress < 100 && (
                          <button
                            className="btn btn-sm btn-danger mt-2"
                            onClick={() => removeFile(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                className="btn btn-success mt-3"
                disabled={uploading || files.length === 0}
                onClick={startAutomation}
              >
                {uploading ? "Processing..." : "Start Automation"}
              </button>

              <hr />

              <h6>Upload Vendor Excel</h6>
              <input
                type="file"
                accept=".xls,.xlsx"
                ref={vendorExcelRef}
                className="form-control"
                onChange={uploadVendorExcel}
                disabled={vendorExcelUploading}
              />

              {vendorExcelUploading && (
                <div className="d-flex align-items-center mt-3">
                  <img
                    src={loaderGif}
                    alt="Uploading..."
                    width={40}
                    height={40}
                    className="me-2"
                  />
                  <span className="text-muted">
                    Uploading vendor excel, please wait...
                  </span>
                </div>
              )}

            </div>
          )}

          {/*{uploading && (
            <div className="card d-flex justify-content-center align-items-center p-4 mb-4">
              <img src={loaderGif} width={120} alt="loading" />
              <div className="mt-3">
                Auto-crop → AI → Area → DXF → Excel
              </div>
            </div>
          )}*/}

          

          {/* COMPARISON */}
          <div className="card p-3">

            <h5 className="mb-3">Vendor vs Automation Comparison</h5>

            {/* STATUS BOX TABS */}
            <div className="row g-2 mb-3 status-box-container">
              {[
                {
                  key: "ALL",
                  label: "All",
                  cls: "status-all",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="2" y="2" width="5" height="5" />
                      <rect x="9" y="2" width="5" height="5" />
                      <rect x="2" y="9" width="5" height="5" />
                      <rect x="9" y="9" width="5" height="5" />
                    </svg>
                  )
                },
                {
                  key: "NOT_PROCESSED",
                  label: "Not Processed",
                  cls: "status-pending",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" />
                      <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" />
                    </svg>
                  )
                },
                {
                  key: "FAILED",
                  label: "Failed (Manual)",
                  cls: "status-failed",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" />
                      <line x1="8" y1="4" x2="8" y2="9" stroke="currentColor"/>
                      <circle cx="8" cy="11.5" r="1" fill="currentColor" />
                    </svg>
                  )
                },
                {
                  key: "MISMATCH",
                  label: "Mismatch",
                  cls: "status-mismatch",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <polygon points="8,1 15,14 1,14" stroke="currentColor"/>
                      <line x1="8" y1="5" x2="8" y2="9" stroke="currentColor"/>
                      <circle cx="8" cy="11.5" r="1" fill="currentColor" />
                    </svg>
                  )
                },
                {
                  key: "APPROVED",
                  label: "Approved",
                  cls: "status-approved",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.485 1.929l-7.07 7.071-2.828-2.829-1.414 1.414 4.242 4.243 8.485-8.485z"/>
                    </svg>
                  )
                },
                {
                  key: "NOT_APPROVED",
                  label: "Not Approved",
                  cls: "status-rejected",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" />
                      <line x1="5" y1="5" x2="11" y2="11" stroke="currentColor" />
                      <line x1="11" y1="5" x2="5" y2="11" stroke="currentColor" />
                    </svg>
                  )
                },
                {
                  key: "PENDING",
                  label: "Pending Approval",
                  cls: "status-pending",
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" />
                      <path d="M8 4v4l3 2" stroke="currentColor"/>
                    </svg>
                  )
                }
              ].map(t => (
                <div className="col-auto" key={t.key}>
                  <button
                    type="button"
                    className={`status-box ${t.cls} ${
                      activeTab === t.key ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    <span className="status-icon">{t.icon}</span>
                    <span>{t.label}</span>
                  </button>
                </div>
              ))}
            </div>


            <div className="row mb-3">
              <div className="col-md-4">
                <input
                  className="form-control"
                  placeholder="Search Sheet"
                  value={compareFilters.sheet}
                  onChange={e =>
                    setCompareFilters({ ...compareFilters, sheet: e.target.value })
                  }
                />
              </div>

              {/* <div className="col-md-3">
                <select
                  className="form-select"
                  value={compareFilters.status}
                  onChange={e =>
                    setCompareFilters({ ...compareFilters, status: e.target.value })
                  }
                >
                  <option value="ALL">All</option>
                  <option value="OK">OK</option>
                  <option value="MISMATCH">Mismatch</option>
                  <option value="NOT_PROCESSED">Not Processed</option>
                </select>
              </div>
              */}

              <div className="col-md-2">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Min %"
                  value={compareFilters.minDiff}
                  onChange={e =>
                    setCompareFilters({ ...compareFilters, minDiff: e.target.value })
                  }
                />
              </div>

              <div className="col-md-2">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Max %"
                  value={compareFilters.maxDiff}
                  onChange={e =>
                    setCompareFilters({ ...compareFilters, maxDiff: e.target.value })
                  }
                />
              </div>

              <div className="col-md-2 d-flex align-items-end gap-2">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={resetFilters}
                >
                  Reset
                </button>

                <button
                  className="btn btn-outline-success w-100"
                  onClick={downloadComparisonExcel}
                >
                  Excel
                </button>

              </div>

              <div className="col-md-2 d-flex align-items-end gap-2">
                <button
                    className="btn btn-outline-danger"
                    disabled={selectedSheets.length === 0}
                    onClick={() => {
                      Swal.fire({
                        title: "Delete Selected Sheets?",
                        text: `You are deleting ${selectedSheets.length} sheet(s).`,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Yes, Delete"
                      }).then(async res => {
                        if (!res.isConfirmed) return;

                        try {
                          await axiosInstance.post(
                            "/api/leather/delete-multiple",
                            { sheets: selectedSheets }
                          );

                          Swal.fire("Deleted", "Sheets removed successfully", "success");
                          setSelectedSheets([]);
                          loadData(); //refresh from DB

                        } catch (err) {
                          Swal.fire("Error", "Delete failed", "error");
                        }
                      });
                    }}
                  >
                    Delete Selected
                  </button>
              </div>

            </div>

            <div className="row mb-3">
              {/* BULK DELETE */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted">
                  Selected: {selectedSheets.length}
                </div>
              </div>
            </div>

            {/* FAILED TAB WARNING */}
            {activeTab === "FAILED" &&
             filteredCompare.length > 0 &&
             !hideFailedAlert && (
              <div className="alert alert-warning d-flex align-items-start position-relative" role="alert">

                {/* CLOSE BUTTON */}
                <button
                  type="button"
                  className="btn-close position-absolute top-0 end-0 m-2"
                  aria-label="Close"
                  onClick={() => setHideFailedAlert(true)}
                ></button>

                {/* ICON */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-exclamation-triangle-fill me-2 mt-1"
                  viewBox="0 0 16 16"
                >
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.964 0L.165 13.233c-.457.778.091 1.767.982 1.767h13.706c.89 0 1.438-.99.982-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>

                <div>
                  <strong>Warning:</strong> Some images failed during processing.<br />
                  <strong>
                    (Note: Image was not detected or could not be cropped properly.
                    Please upload a new image with clear visibility.)
                  </strong>
                </div>
              </div>
            )}

            {/* MISMATCH TAB WARNING */}
            {activeTab === "MISMATCH" &&
             filteredCompare.length > 0 &&
             !hideMismatchAlert && (
              <div className="alert alert-danger d-flex align-items-start position-relative" role="alert">

                {/* CLOSE BUTTON */}
                <button
                  type="button"
                  className="btn-close position-absolute top-0 end-0 m-2"
                  aria-label="Close"
                  onClick={() => setHideMismatchAlert(true)}
                ></button>

                {/* ICON */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-exclamation-octagon-fill me-2 mt-1"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.46.146a.5.5 0 0 0-.353-.146H4.893a.5.5 0 0 0-.353.146L.146 4.54A.5.5 0 0 0 0 4.893v6.214a.5.5 0 0 0 .146.353l4.394 4.394a.5.5 0 0 0 .353.146h6.214a.5.5 0 0 0 .353-.146l4.394-4.394a.5.5 0 0 0 .146-.353V4.893a.5.5 0 0 0-.146-.353L11.46.146zM8 4c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995A.905.905 0 0 1 8 4zm0 7a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>

                <div>
                  <strong>Mismatch Detected:</strong> Vendor area and automation area do not match.<br />
                  <strong>
                    (Note: Please verify the image quality and vendor data, then re-upload if required.)
                  </strong>
                </div>
              </div>
            )}


            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={
                        filteredCompare.length > 0 &&
                        selectedSheets.length === filteredCompare.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSheets(
                            filteredCompare.map(r => r.SheetName)
                          );
                        } else {
                          setSelectedSheets([]);
                        }
                      }}
                    />
                  </th>

                  <th>Sheet</th>

                  <th>Vendor Area</th>
                  <th>Auto Area</th>
                  <th>Diff %</th>
                  <th>Status</th>
                  <th>Download DXF</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompare.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-3">
                      No Data Found
                    </td>
                  </tr>
                ) : (
                  filteredCompare.map((c, i) => (
                    <tr
                      key={i}
                      className={
                        c.Status === "MISMATCH"
                          ? "table-danger"
                          : c.Status === "NOT_PROCESSED"
                          ? "table-warning"
                          : ""
                      }
                    >
                      <td className="text-center align-middle">
                        <div className="form-check d-flex justify-content-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            /*disabled={c.Status === "OK" && c.IsApproved}*/
                            checked={isSheetSelected(c.SheetName)}
                            onChange={() => toggleSheetSelect(c.SheetName)}
                          />
                        </div>
                      </td>

                      <td>{c.SheetName}</td>

                      <td>{c.VendorArea_SqFt}</td>
                      <td>{c.AutoArea_SqFt ?? "-"}</td>
                      <td>{c.Diff_Percent ?? "-"}</td>
                      <td>
                        {c.Status === "FAILED" && (
                          <>
                            <span className="badge bg-danger">FAILED</span><br />
                          </>
                        )}

                        {c.Status === "NOT_PROCESSED" && (
                          <span className="badge bg-warning">NOT PROCESSED</span>
                        )}

                        {c.Status === "OK" && (
                          <span className="badge bg-success">OK</span>
                        )}

                        {c.Status === "MISMATCH" && (
                          <span className="badge bg-danger">Mismatch</span>
                        )}
                      </td>

                      <td className="d-flex1 gap-1">

                        {/* DXF */}
                        {c.DXFPath && (
                          <a
                            href={`${axiosInstance.defaults.baseURL}/${c.DXFPath.replace(/\\/g, "/")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-success"
                          >
                            DXF
                          </a>
                        )}

                      </td>

                      <td>
                        {/* ACTIONS */}
                          {c.Status === "OK" && !c.IsApproved && (
                            <>
                              <button
                                className="btn btn-sm btn-success me-2"
                                onClick={() => handleApprove(c.SheetName)}
                              >
                                Approve
                              </button>

                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleReject(c.SheetName)}
                              >
                                Reject
                              </button>
                            </>
                          )}

                          {c.IsApproved && (
                            <span className="badge bg-success">APPROVED</span>
                          )}

                          {(c.Status === "FAILED" || c.Status === "MISMATCH") && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary mt-1"
                                disabled={reUploadingSheet === c.SheetName}
                                onClick={() => {
                                  setReUploadingSheet(c.SheetName);
                                  reuploadInputRef.current.click();
                                }}
                              >
                                {reUploadingSheet === c.SheetName
                                  ? "Re-uploading..."
                                  : "Re-Upload"}
                              </button>

                              <input
                                type="file"
                                accept="image/*"
                                ref={reuploadInputRef}
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;

                                  handleReUpload(file, reUploadingSheet);
                                  e.target.value = null; // IMPORTANT
                                }}
                              />

                              {reUploadingSheet === c.SheetName && (
                                <div className="text-muted small mt-1">
                                  Processing…
                                </div>
                              )}
                            </>
                          )}


                      </td>

                    </tr>
                  ))
                )}
              </tbody>

            </table>
          </div>

        </main>
      </div>
    </div>
  );
}
